import { useQuery } from "@tanstack/react-query";
import { adminService } from "../services/admin.service";

/**
 * Effective permissions for the signed-in staff member.
 *
 * Used to decide which admin navigation entries and action buttons to render.
 * This is presentation only - the backend independently enforces every
 * permission, so hiding a button is a usability choice, never a security
 * control.
 *
 * Permission strings mirror `app/core/permissions.py`; see
 * `backend/docs/rbac.md` for the full matrix.
 */
export const PERMISSIONS = {
  USERS_READ: "users:read",
  USERS_WRITE: "users:write",
  USERS_ROLE_WRITE: "users:role:write",
  COMPLIANCE_READ: "compliance:read",
  COMPLIANCE_DECIDE: "compliance:decide",
  AML_READ: "aml:read",
  TRANSFERS_READ: "transfers:read",
  TRANSFERS_EXECUTE: "transfers:execute",
  LEDGER_READ: "ledger:read",
  RECONCILIATION_READ: "reconciliation:read",
  RECONCILIATION_WRITE: "reconciliation:write",
  ROUTING_READ: "routing:read",
  ROUTING_WRITE: "routing:write",
  LLM_READ: "llm:read",
  LLM_WRITE: "llm:write",
  CONVERSATIONS_READ: "conversations:read",
  AUDIT_READ: "audit:read",
  METRICS_READ: "metrics:read",
};

export function usePermissions() {
  const { data, isLoading, error } = useQuery({
    queryKey: ["auth", "permissions"],
    queryFn: adminService.getPermissions,
    staleTime: 5 * 60_000,
    retry: false,
  });

  const granted = data?.permissions ?? [];

  return {
    role: data?.role ?? null,
    permissions: granted,
    /** True when the signed-in user holds every listed permission. */
    can: (...required) => required.every((item) => granted.includes(item)),
    /** True when the signed-in user holds at least one listed permission. */
    canAny: (...required) => required.some((item) => granted.includes(item)),
    /** True for any staff member (a customer holds no permissions at all). */
    isStaff: granted.length > 0,
    isLoadingPermissions: isLoading,
    permissionsError: error,
  };
}
